import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useSubscriptionStore } from '@/stores/subscription';
import { useOrganizationStore } from '@/stores/organization';

// setTimeout fires at once for delays above 2^31 - 1 ms.
const MAX_DELAY_MS = 2_147_483_647;

/**
 * Whether the loaded plan is entitled, re-evaluated when it expires: a timer set for `expiresAt` advances the clock
 * the rule is evaluated with, and is cleared when the component unmounts.
 */
export function useEntitlement() {
  const store = useSubscriptionStore();
  const now = ref(Date.now());
  let timer: ReturnType<typeof setTimeout> | undefined;

  function schedule() {
    clearTimeout(timer);
    timer = undefined;
    now.value = Date.now();
    const expiresAt = Date.parse(store.subscription?.expiresAt ?? '');
    if (!Number.isFinite(expiresAt) || expiresAt <= now.value) return;
    timer = setTimeout(schedule, Math.min(expiresAt - now.value + 1, MAX_DELAY_MS));
  }

  watch(() => store.subscription, schedule, { immediate: true });
  onBeforeUnmount(() => clearTimeout(timer));

  return computed(() => store.entitledAt(now.value));
}

/**
 * Whether the plan of the selected organization is loaded and not entitled. An unknown plan does not count: the
 * backend refuses the actions either way.
 */
export function usePlanRequired() {
  const store = useSubscriptionStore();
  const organizations = useOrganizationStore();
  const entitled = useEntitlement();
  const planRequired = computed(
    () =>
      store.organizationId !== undefined &&
      store.organizationId === organizations.selectedId &&
      !entitled.value
  );

  /** Reloads the plan; false when that failed and the loaded plan is unchanged. */
  async function refresh(): Promise<boolean> {
    try {
      await store.fetchSubscription();
      return true;
    } catch {
      return false;
    }
  }

  return { planRequired, refresh };
}
